import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { BarChart3, ArrowRight, ArrowLeft, Download } from 'lucide-react';
import PageLayout from '@/components/layout/PageLayout';
import SapSubNav from '@/components/sap/SapSubNav';
import {
  ASSESSMENT_QUESTIONS, RadarChart, LeadGateModal,
  generateAssessmentReport, getGrade,
} from '@/components/sap/shared';

const SapAssessmentPage = () => {
  const [started, setStarted] = useState(false);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [showLeadGate, setShowLeadGate] = useState(false);
  const [leadCaptured, setLeadCaptured] = useState(false);

  const currentQ = ASSESSMENT_QUESTIONS[step];
  const progress = Object.keys(answers).length / ASSESSMENT_QUESTIONS.length;

  const handleAnswer = (score) => {
    setAnswers(prev => ({ ...prev, [currentQ.id]: score }));
    if (step < ASSESSMENT_QUESTIONS.length - 1) setTimeout(() => setStep(s => s + 1), 200);
    else setTimeout(() => setShowResults(true), 300);
  };

  const scores = ASSESSMENT_QUESTIONS.map(q => answers[q.id] || 0);
  const avgScore = scores.reduce((a, b) => a + b, 0) / scores.length;
  const overallPct = Math.round((avgScore / 4) * 100);
  const grade = getGrade(overallPct);

  const handleDownload = () => {
    if (leadCaptured) generateAssessmentReport(scores, overallPct, grade, leadCaptured);
    else setShowLeadGate(true);
  };

  return (
    <PageLayout>
      <SapSubNav />
      <LeadGateModal open={showLeadGate} onClose={() => setShowLeadGate(false)} onSuccess={(form) => { setLeadCaptured(form.name); setShowLeadGate(false); generateAssessmentReport(scores, overallPct, grade, form.name); }} title="Download Your Report" subtitle="Get your personalized readiness scorecard as a PDF." source="assessment_report" />

      <section className="pb-8 pt-6" data-testid="assessment-hero">
        <div className="max-w-4xl mx-auto px-6">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-taxera-teal/10 text-taxera-teal text-sm font-medium rounded-full mb-4">
            <BarChart3 className="w-4 h-4" strokeWidth={1.5} /> Migration Readiness Assessment
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-taxera-charcoal leading-[1.1] mb-4">
            Are you ready for <span className="taxera-gradient-text">S/4HANA?</span>
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl">Assess your compliance readiness across 8 critical dimensions. Free for everyone. PDF report requires a quick registration.</p>
        </div>
      </section>

      <section className="pb-20" data-testid="assessment-body">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-8">
            {!started && !showResults && (
              <div className="text-center py-8">
                <h3 className="font-heading text-xl font-bold text-taxera-charcoal mb-3">8 questions. 2 minutes.</h3>
                <p className="text-sm text-slate-500 mb-6 max-w-md mx-auto">Get a personalized scorecard across Cloud, Coverage, Integration, Automation, and more.</p>
                <button onClick={() => setStarted(true)} className="group inline-flex items-center gap-2 px-8 py-4 taxera-gradient text-white rounded-full font-semibold hover:shadow-lg transition-all hover:scale-105" data-testid="assessment-start-btn">
                  Start Assessment <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            )}

            {started && !showResults && (
              <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} data-testid="assessment-question">
                <div className="mb-6">
                  <div className="flex items-center justify-between text-xs text-slate-400 mb-2">
                    <span>Question {step + 1} of {ASSESSMENT_QUESTIONS.length}</span>
                    <span className="font-semibold">{currentQ.dimension}</span>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <motion.div className="h-full bg-taxera-teal rounded-full" animate={{ width: `${progress * 100}%` }} />
                  </div>
                </div>
                <h3 className="font-heading text-lg font-bold text-taxera-charcoal mb-5">{currentQ.question}</h3>
                <div className="space-y-2.5">
                  {currentQ.options.map((opt, i) => (
                    <button key={i} onClick={() => handleAnswer(opt.score)} className={`w-full text-left p-4 rounded-xl border transition-all hover:shadow-md ${answers[currentQ.id] === opt.score ? 'border-taxera-teal bg-taxera-teal/5' : 'border-slate-200 bg-white hover:border-slate-300'}`} data-testid={`assessment-option-${i}`}>
                      <span className="text-sm text-slate-700">{opt.label}</span>
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {showResults && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="assessment-results">
                <div className="grid lg:grid-cols-2 gap-8 items-center">
                  <div>
                    <div className="text-2xs font-bold text-slate-400 uppercase tracking-[0.15em] mb-3">Your readiness score</div>
                    <div className="flex items-baseline gap-3 mb-2">
                      <span className="font-heading text-6xl font-bold" style={{ color: grade.color }}>{overallPct}%</span>
                      <span className="text-lg font-bold" style={{ color: grade.color }}>{grade.label}</span>
                    </div>
                    <p className="text-sm text-slate-600 leading-relaxed mb-6">{grade.desc}</p>
                    <div className="space-y-2 mb-6">
                      {ASSESSMENT_QUESTIONS.map((q, i) => (
                        <div key={q.id} className="flex items-center gap-3">
                          <span className="text-xs text-slate-400 w-28 shrink-0 font-medium">{q.dimension}</span>
                          <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full rounded-full" style={{ width: `${(scores[i]/4)*100}%`, backgroundColor: scores[i] >= 3 ? '#10b981' : scores[i] >= 2 ? '#f59e0b' : '#ef4444' }} /></div>
                          <span className="text-xs font-bold text-slate-500 w-6 text-right">{scores[i]}/4</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex flex-wrap gap-3">
                      <Link to="/contact" className="group inline-flex items-center gap-2 px-6 py-3 taxera-gradient text-white rounded-full text-sm font-semibold hover:shadow-lg transition-all" data-testid="assessment-cta-expert">Discuss with an Expert <ArrowRight className="w-4 h-4" /></Link>
                      <button onClick={handleDownload} className="inline-flex items-center gap-2 px-6 py-3 bg-white border border-slate-200 text-slate-600 rounded-full text-sm font-semibold hover:bg-slate-50" data-testid="assessment-download-btn"><Download className="w-3.5 h-3.5" /> Download PDF Report</button>
                    </div>
                  </div>
                  <RadarChart scores={scores} dimensions={ASSESSMENT_QUESTIONS.map(q => q.dimension)} />
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default SapAssessmentPage;
